'use client';

import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFormatter, useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';

import { Button } from '@/src/shared/ui/button';

import { mfaChallengeSchema } from '../model/schemas';
import type { AuthErrorCode, MfaChallengeFormValues } from '../model/types';
import { verifyMfaLoginAction } from '../api/verifyMfaLoginAction';
import { isRedirectError } from '../lib/isRedirectError';
import { MfaCodeField } from './MfaCodeField';

const MFA_API_ERRORS = [
  'invalid_code',
  'rate_limited',
  'mfa_locked',
  'mfa_locked_support',
  'server_error',
] as const;
type MfaApiError = (typeof MFA_API_ERRORS)[number];

function toMfaApiError(code: AuthErrorCode): MfaApiError {
  return (MFA_API_ERRORS as readonly string[]).includes(code)
    ? (code as MfaApiError)
    : 'server_error';
}

export function MfaChallengeForm() {
  const t = useTranslations('auth.mfa');
  const params = useParams();
  const locale = (params.locale as string) ?? 'es';
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<MfaApiError | null>(null);
  const [lockedUntil, setLockedUntil] = useState<string>();
  const format = useFormatter();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MfaChallengeFormValues>({
    resolver: yupResolver(mfaChallengeSchema),
    defaultValues: { method: 'totp', code: '' },
  });
  const method = useWatch({ control, name: 'method' });
  // The API locks the account's MFA, not one factor, so every code fails until
  // the lock ends; switching method keeps the lock and its message.
  const locked = apiError === 'mfa_locked' || apiError === 'mfa_locked_support';

  function switchMethod() {
    reset({ method: method === 'totp' ? 'backup' : 'totp', code: '' });
    if (!locked) setApiError(null);
  }

  async function onSubmit(data: MfaChallengeFormValues) {
    setSubmitting(true);
    setApiError(null);
    try {
      const result = await verifyMfaLoginAction({ code: data.code, method: data.method }, locale);
      if (result && !result.ok) {
        setApiError(toMfaApiError(result.code));
        setLockedUntil(result.lockedUntil);
        setSubmitting(false);
      }
    } catch (err) {
      // A verified code redirects, and Next signals that by throwing.
      if (isRedirectError(err)) throw err;
      setApiError('server_error');
      setSubmitting(false);
    }
  }

  return (
    <form
      method="post"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex w-full flex-col gap-4"
    >
      <div className="flex flex-col gap-1.5 text-center">
        <h1 className="text-lg font-medium">{t('title')}</h1>
        <p className="text-sm text-muted-foreground">
          {method === 'backup' ? t('backupSubtitle') : t('subtitle')}
        </p>
      </div>

      <MfaCodeField
        key={method}
        control={control}
        errors={errors}
        method={method}
        rejected={apiError === 'invalid_code'}
      />

      {apiError && (
        <p id="mfa-api-error" role="alert" className="text-xs text-destructive">
          {apiError === 'mfa_locked' && lockedUntil
            ? t('errors.mfa_locked_until', {
                time: format.dateTime(new Date(lockedUntil), { timeStyle: 'short' }),
              })
            : t(`errors.${apiError}`)}
        </p>
      )}

      <Button
        type="submit"
        disabled={submitting || locked}
        className="h-11 w-full rounded-lg text-[14px]"
      >
        {submitting ? (
          <>
            <Loader2 className="animate-spin" />
            {t('submitting')}
          </>
        ) : (
          t('submit')
        )}
      </Button>

      {/* A recovery code is for a lost authenticator, not a second way in, so
          it sits behind a quiet link like GitHub's and Google's. */}
      <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground">
        <Button
          type="button"
          variant="link"
          disabled={submitting}
          onClick={switchMethod}
          className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
        >
          {method === 'totp' ? t('cantUseApp') : t('useApp')}
        </Button>
        <Link
          href={`/${locale}/login`}
          className="hover:text-foreground underline-offset-4 hover:underline"
        >
          {t('backToLogin')}
        </Link>
      </div>
    </form>
  );
}
